import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CustomFieldType, CustomFieldValidators, } from '@tribeplatform/gql-client/types';
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown';
import { dayjs } from '../common';
import { Form } from '../Form';
import { SimpleHtmlEditor } from '../HtmlEditor/SimpleHtmlEditor';
import { useI18n } from '../i18n';
import { CustomFieldSubtype, getFieldSetting } from './utils';
const BooleanInput = ({ name, onChange, value, subtype, extraProps }) => {
    var _a;
    const intl = useI18n();
    if (extraProps === null || extraProps === void 0 ? void 0 : extraProps.label) {
        if (subtype === CustomFieldSubtype.CHECKBOX) {
            return (_jsx(Form.Checkbox, { name: name, onChange: value => {
                    if (typeof onChange === 'function')
                        onChange(value);
                }, ...extraProps }, void 0));
        }
        return (_jsx(Form.Toggle, { name: name, onChange: value => {
                if (typeof onChange === 'function')
                    onChange(value);
            }, ...extraProps }, void 0));
    }
    const items = [
        {
            text: intl.$t({
                id: 'CustomField.FieldInput.Checked',
                defaultMessage: 'Checked',
            }),
            value: true,
        },
        {
            text: intl.$t({
                id: 'CustomField.FieldInput.Unchecked',
                defaultMessage: 'Unchecked',
            }),
            value: false,
        },
    ];
    return (_jsxs(Dropdown, { children: [_jsx(Dropdown.Button, { children: (_a = items === null || items === void 0 ? void 0 : items.find(item => item.value === value)) === null || _a === void 0 ? void 0 : _a.text }, void 0), _jsx(Dropdown.Items, { children: items.map(item => (_jsx(Dropdown.Item, { onClick: () => {
                        if (typeof onChange === 'function')
                            onChange(item.value);
                    }, children: item.text }, name))) }, void 0)] }, void 0));
};
const ArrayInput = ({ field, name, onChange, value, extraProps }) => {
    var _a, _b, _c, _d;
    if ((_b = (_a = field === null || field === void 0 ? void 0 : field.items) === null || _a === void 0 ? void 0 : _a.validators) === null || _b === void 0 ? void 0 : _b.length) {
        const enumValidator = (_d = (_c = field === null || field === void 0 ? void 0 : field.items) === null || _c === void 0 ? void 0 : _c.validators) === null || _d === void 0 ? void 0 : _d.find(validator => validator.validation === CustomFieldValidators.enum);
        if (enumValidator === null || enumValidator === void 0 ? void 0 : enumValidator.value) {
            try {
                const validValues = JSON.parse(enumValidator.value);
                if (extraProps === null || extraProps === void 0 ? void 0 : extraProps.label) {
                    return (_jsx(Form.Multiselect, { name: name, onChange: value => {
                            if (typeof onChange === 'function')
                                onChange([value]);
                        }, options: validValues, ...extraProps }, void 0));
                }
                return (_jsxs(Dropdown, { children: [_jsx(Dropdown.Button, { children: value }, void 0), _jsx(Dropdown.Items, { children: validValues.map(value => (_jsx(Dropdown.Item, { onClick: () => {
                                    if (typeof onChange === 'function')
                                        onChange(value);
                                }, children: value }, value))) }, void 0)] }, void 0));
            }
            catch (e) {
                console.error('Could not parse enum array custom field validator');
            }
        }
    }
    return null;
};
const TextareaInput = ({ name, onChange, extraProps }) => {
    return (_jsx(Form.Textarea, { name: name, onChange: e => {
            if (typeof onChange === 'function')
                onChange(e.target.value);
        }, className: "py-1.5", rows: !(extraProps === null || extraProps === void 0 ? void 0 : extraProps.label) ? 1 : 4, ...extraProps }, void 0));
};
const SelectInput = ({ name, onChange, value, extraProps, enumValidator }) => {
    try {
        const validValues = JSON.parse(enumValidator.value);
        if (extraProps === null || extraProps === void 0 ? void 0 : extraProps.label) {
            return (_jsx(Form.Select, { name: name, items: validValues === null || validValues === void 0 ? void 0 : validValues.map(value => ({
                    text: value || '-',
                    value,
                })), onChange: value => {
                    if (typeof onChange === 'function')
                        onChange(value);
                }, ...extraProps }, void 0));
        }
        return (_jsxs(Dropdown, { children: [_jsx(Dropdown.Button, { children: value }, void 0), _jsx(Dropdown.Items, { children: validValues.map(value => (_jsx(Dropdown.Item, { onClick: () => {
                            if (typeof onChange === 'function')
                                onChange(value);
                        }, children: value }, name))) }, void 0)] }, void 0));
    }
    catch (e) {
        console.error('Could not parse enum custom field validator');
    }
    return null;
};
const NumberInput = ({ name, onChange, subtype, extraProps }) => {
    const intl = useI18n();
    if (subtype === CustomFieldSubtype.RATING) {
        extraProps = {
            ...extraProps,
            ...{
                min: 0,
                max: 5,
                step: 1,
                placeholder: (extraProps === null || extraProps === void 0 ? void 0 : extraProps.placeholder) ||
                    intl.$t({
                        id: 'CustomField.FieldInput.DefaultNumberInputPlaceholder',
                        defaultMessage: 'Between 0 and 5',
                    }),
            },
        };
    }
    return (_jsx(Form.Input, { name: name, type: "number", onChange: e => {
            if (typeof onChange === 'function')
                onChange(e.target.value);
        }, className: "py-1.5", ...extraProps }, void 0));
};
const TextInput = ({ field, name, onChange, subtype, extraProps }) => {
    let inputType;
    if (subtype === CustomFieldSubtype.URL)
        inputType = 'url';
    else if (subtype === CustomFieldSubtype.EMAIL)
        inputType = 'email';
    else if (subtype === CustomFieldSubtype.DATETIME)
        inputType = 'datetime-local';
    else if ((field === null || field === void 0 ? void 0 : field.type) === CustomFieldType.number)
        inputType = 'number';
    else if ((field === null || field === void 0 ? void 0 : field.type) === CustomFieldType.date)
        inputType = 'date';
    if (subtype === CustomFieldSubtype.DATETIME)
        extraProps.trailingAddon = `${dayjs.tz.guess()}`;
    return (_jsx(Form.Input, { name: name, type: inputType || 'text', onChange: e => {
            if (typeof onChange === 'function')
                onChange(e.target.value);
        }, className: "py-1.5", ...extraProps }, void 0));
};
export const FieldInput = ({ field, name = undefined, onChange = undefined, value = undefined, ...extraProps }) => {
    var _a, _b;
    const intl = useI18n();
    if (!field) {
        return null;
    }
    name = name || field.accessor || field.key;
    const subtype = getFieldSetting(field, 'subtype') || field.subtype;
    if (!extraProps)
        extraProps = {};
    if (field === null || field === void 0 ? void 0 : field.required)
        extraProps.validation = {
            required: intl.$t({
                id: 'Generic.FieldIsRequired',
                defaultMessage: 'This field is required.',
            }),
        };
    if (value)
        extraProps.value = value;
    const inputProps = {
        field,
        name,
        onChange,
        value,
        extraProps,
        subtype,
        validation: undefined,
    };
    const enumValidator = (_a = field === null || field === void 0 ? void 0 : field.validators) === null || _a === void 0 ? void 0 : _a.find(validator => validator.validation === CustomFieldValidators.enum);
    switch (field.type) {
        case CustomFieldType.boolean:
            return _jsx(BooleanInput, { ...inputProps }, void 0);
        case CustomFieldType.richText:
            return (_jsx("div", { children: _jsx(SimpleHtmlEditor, { name: name, ...extraProps }, void 0) }, void 0));
        case CustomFieldType.text:
            if (enumValidator === null || enumValidator === void 0 ? void 0 : enumValidator.value)
                return _jsx(SelectInput, { ...inputProps, enumValidator: enumValidator }, void 0);
            if (subtype === CustomFieldSubtype.TEXTAREA)
                return _jsx(TextareaInput, { ...inputProps }, void 0);
            return _jsx(TextInput, { ...inputProps }, void 0);
        case CustomFieldType.number:
            return _jsx(NumberInput, { ...inputProps }, void 0);
        case CustomFieldType.date:
            return _jsx(TextInput, { ...inputProps }, void 0);
        case CustomFieldType.relation:
        case CustomFieldType.array:
            switch (subtype) {
                case CustomFieldSubtype.SELECT:
                case CustomFieldSubtype.MULTISELECT:
                    return _jsx(ArrayInput, { ...inputProps }, void 0);
                case CustomFieldSubtype.IMAGE:
                case CustomFieldSubtype.IMAGES:
                    return (_jsx(Form.Image, { variant: "default", multiple: subtype === CustomFieldSubtype.IMAGES, ...inputProps, ...extraProps }, void 0));
                case CustomFieldSubtype.FILE:
                case CustomFieldSubtype.FILES:
                    return (_jsx(Form.File, { multiple: subtype === CustomFieldSubtype.FILES, ...inputProps, ...extraProps }, void 0));
                case CustomFieldSubtype.MEMBER:
                case CustomFieldSubtype.MEMBERS:
                    return (_jsx(Form.MemberPicker, { ...extraProps, ...inputProps, memberGroup: ((_b = getFieldSetting(field, 'inputProps')) === null || _b === void 0 ? void 0 : _b.memberGroup) ||
                            intl.$t({
                                id: 'CustomField.FieldInput.DefaultMemberGroup',
                                defaultMessage: 'staff',
                            }), onChange: values => {
                            if (typeof onChange === 'function')
                                onChange(values === null || values === void 0 ? void 0 : values.id);
                        }, multiple: subtype === CustomFieldSubtype.MEMBERS }, void 0));
                case CustomFieldSubtype.TAG:
                case CustomFieldSubtype.TAGS:
                    return (_jsx(Form.TagPicker, { ...extraProps, ...inputProps, onChange: values => {
                            if (typeof onChange === 'function')
                                onChange(values === null || values === void 0 ? void 0 : values.id);
                        }, multiple: subtype === CustomFieldSubtype.TAGS }, void 0));
                case CustomFieldSubtype.SPACE:
                case CustomFieldSubtype.SPACES:
                    return (_jsx(Form.SpacePicker, { ...extraProps, ...inputProps, onChange: values => {
                            if (typeof onChange === 'function')
                                onChange(values === null || values === void 0 ? void 0 : values.id);
                        }, multiple: subtype === CustomFieldSubtype.SPACES }, void 0));
                default:
                    return null;
            }
        default:
            return null;
    }
};
//# sourceMappingURL=FieldInput.js.map